import { Flex } from '@common/components/Flex';
import { FormControl } from '@common/components/form-control';
import { DatasheetGridSettingsDto, DatasheetGridSettingsMarginType } from '@common/dto/datasheet/grid-settings-dto';
import { getTestAttributes } from '@common/utils/tests';
import { trDts } from '@modules/datasheets/utils/i18n';
import React from 'react';
import { Checkbox } from '@common/components/checkbox';
import { Radio } from '@common/components/radio';
import { Button } from '@common/components/Button';

import './grid-settings.styles.scss';

export type GridSettingsProps = {
  settings: DatasheetGridSettingsDto;
  onChange: (settings: DatasheetGridSettingsDto) => void;
  onResetTabOrder: () => void;
};

export const GridSettings: React.FC<GridSettingsProps> = (props) => {
  const { settings, onChange, onResetTabOrder } = props;
  const [settingsLocal, setSettingsLocal] = React.useState<DatasheetGridSettingsDto>(settings);
  const [customMargin, setCustomMargin] = React.useState<{ width: number; height: number }>({
    width: 400,
    height: 225,
  });
  const [customMarginInput, setCustomMarginInput] = React.useState<{ width: string; height: string }>({
    width: "400",
    height: "225",
  });
  const [horizontalGridInput, setHorizontalGridInput] = React.useState<string>(String(settings.horizontalGrid ?? 25));
  const [verticalGridInput, setVerticalGridInput] = React.useState<string>(String(settings.verticalGrid ?? 25));

  const sanitizePositiveIntInput = React.useCallback((value: string) => {
    return value.replace(/[^0-9]/g, "");
  }, []);

  const MARGIN_PRESETS: Record<DatasheetGridSettingsMarginType, { width: number; height: number }> = {
    [DatasheetGridSettingsMarginType.fhq]: { width: 1100, height: 450 },
    [DatasheetGridSettingsMarginType.tablet]: { width: 300, height: 650 },
    [DatasheetGridSettingsMarginType.custom]: { width: customMargin.width, height: customMargin.height },
  };

  const parsePositiveNumber = React.useCallback(
    (value: string | number, fallback: number) => {
      const next = Number(value);
      if (!Number.isFinite(next) || next <= 0) {
        return fallback;
      }
      return next;
    },
    []
  );

  React.useEffect(() => {
    setSettingsLocal(settings);

    setHorizontalGridInput((prev) => {
      const next = String(settings.horizontalGrid ?? 30);
      if (prev === "") {
        return prev;
      }
      return next === prev ? prev : next;
    });

    setVerticalGridInput((prev) => {
      const next = String(settings.verticalGrid ?? 30);
      if (prev === "") {
        return prev;
      }
      return next === prev ? prev : next;
    });

    // Keep custom inputs in sync only when custom is active in incoming settings
    if (settings.marginType === DatasheetGridSettingsMarginType.custom) {
      const nextWidth = settings.marginWidth ?? customMargin.width;
      const nextHeight = settings.marginHeight ?? customMargin.height;
      setCustomMargin({
        width: nextWidth,
        height: nextHeight,
      });
      setCustomMarginInput((prev) => ({
        width: prev.width === "" ? prev.width : String(nextWidth),
        height: prev.height === "" ? prev.height : String(nextHeight),
      }));
    }
  }, [settings]);

  const handleInputChange = (data: Partial<DatasheetGridSettingsDto>) => {
    const newSettings = {
      ...settingsLocal,
      ...data,
    };

    setSettingsLocal(newSettings);
    onChange(newSettings);
  };

  const showCheckboxes = [
    {
      id: 'cbx-grid-operation-toolbar-menu-show-input',
      label: trDts('Show Grid'),
      checked: settingsLocal.showGrid,
      onChange: (checked: boolean) => handleInputChange({ showGrid: checked }),
    },
    {
      id: 'cbx-grid-operation-toolbar-menu-show-page-break-input',
      label: trDts('Show Page Breaks'),
      checked: settingsLocal.showPageBreaks,
      onChange: (checked: boolean) => handleInputChange({ showPageBreaks: checked }),
    },
    {
      id: 'cbx-grid-operation-toolbar-menu-snap-to-grid-input',
      label: trDts('Snap to Grid'),
      checked: settingsLocal.snapToGrid,
      onChange: (checked: boolean) => handleInputChange({ snapToGrid: checked }),
    },
    {
      id: 'cbx-grid-operation-toolbar-menu-show-tab-order-input',
      label: trDts('Show/Edit Tab Order'),
      checked: settingsLocal.showEditTabOrder,
      onChange: (checked: boolean) => handleInputChange({ showEditTabOrder: checked }),
      additionalComponent: (
        <Button 
          disabled={!settingsLocal.showEditTabOrder} 
          bsStyle='default' 
          onClick={onResetTabOrder}
          className="DatasheetsEditorHeaderGridSettings__reset-tab-order-button"
        >
          {trDts('Reset Tab Order')}
        </Button>
      ),
    },
  ];

  const gridControls = [
    {
      value: settingsLocal.horizontalGrid,
      id: 'tx-grid-operation-toolbar-menu-grid-x',
      label: trDts('Horizontal Grid'),
      inputValue: horizontalGridInput,
      onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
        const raw = e.target.value;
        const sanitized = sanitizePositiveIntInput(raw);
        setHorizontalGridInput(sanitized);
        const next = Number(sanitized);
        if (Number.isFinite(next) && next > 0) {
          handleInputChange({ horizontalGrid: next });
        }
      },
    },
    {
      value: settingsLocal.verticalGrid,
      id: 'tx-grid-operation-toolbar-menu-grid-y',
      label: trDts('Vertical Grid'),
      inputValue: verticalGridInput,
      onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
        const raw = e.target.value;
        const sanitized = sanitizePositiveIntInput(raw);
        setVerticalGridInput(sanitized);
        const next = Number(sanitized);
        if (Number.isFinite(next) && next > 0) {
          handleInputChange({ verticalGrid: next });
        }
      },
    },
  ];

  return (
    <div className="DatasheetsEditorHeaderGridSettings">
      {showCheckboxes.map((x) => (
        <React.Fragment key={x.label}>
          <Checkbox 
            {...getTestAttributes({ id: x.id })}
            checked={x.checked}
            className="DatasheetsEditorHeaderGridSettings__checkbox"
            label={x.label}
            onChange={x.onChange}
          />
          {x.additionalComponent}
        </React.Fragment>
      ))}

      <Checkbox
        className="DatasheetsEditorHeaderGridSettings__checkbox"
        checked={settingsLocal.showMargins}
        label={trDts('Show Margins')}
        onChange={(checked: boolean) => handleInputChange({ showMargins: checked })}
      />
      <div className="DatasheetsEditorHeaderGridSettings__margins-section">
        <Radio 
          className="DatasheetsEditorHeaderGridSettings__margins-section__radio"
          disabled={!settingsLocal.showMargins}
          {...getTestAttributes({ id: 'radio-grid-operation-toolbar-menu-fhq' })}
          label={trDts('FHQ')}
          checked={settingsLocal.marginType === DatasheetGridSettingsMarginType.fhq}
          onChange={() =>
            handleInputChange({
              marginType: DatasheetGridSettingsMarginType.fhq,
              marginWidth: MARGIN_PRESETS[DatasheetGridSettingsMarginType.fhq].width,
              marginHeight: MARGIN_PRESETS[DatasheetGridSettingsMarginType.fhq].height,
            })
          }
        />
        <Radio 
          className="DatasheetsEditorHeaderGridSettings__margins-section__radio"
          disabled={!settingsLocal.showMargins}
          {...getTestAttributes({ id: 'radio-grid-operation-toolbar-menu-tablet-rb' })}
          label={trDts('Tablet')}
          checked={settingsLocal.marginType === DatasheetGridSettingsMarginType.tablet}
          onChange={() =>
            handleInputChange({
              marginType: DatasheetGridSettingsMarginType.tablet,
              marginWidth: MARGIN_PRESETS[DatasheetGridSettingsMarginType.tablet].width,
              marginHeight: MARGIN_PRESETS[DatasheetGridSettingsMarginType.tablet].height,
            })
          }
        />
        <Flex alignItems="center">
          <Radio 
            className="DatasheetsEditorHeaderGridSettings__margins-section__radio"
            disabled={!settingsLocal.showMargins}
            {...getTestAttributes({ id: 'radio-grid-operation-toolbar-menu-custom-rb' })}
            label={trDts('Custom')}
            checked={settingsLocal.marginType === DatasheetGridSettingsMarginType.custom}
            onChange={() =>
              handleInputChange({
                marginType: DatasheetGridSettingsMarginType.custom,
                marginWidth: customMargin.width,
                marginHeight: customMargin.height,
              })
            }
          />
          <FormControl
            {...getTestAttributes({ id: 'tx-grid-operation-toolbar-menu-custom-marginx' })}
            disabled={settingsLocal.marginType !== DatasheetGridSettingsMarginType.custom}
            type="number"
            className="DatasheetsEditorHeaderGridSettings__margins-section__input"
            value={customMarginInput.width}
            min={1}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const raw = e.target.value;
              const sanitized = sanitizePositiveIntInput(raw);
              setCustomMarginInput((prev) => ({ ...prev, width: sanitized }));
              const next = Number(sanitized);
              if (Number.isFinite(next) && next > 0) {
                setCustomMargin((prev) => ({ ...prev, width: next }));
                if (settingsLocal.marginType === DatasheetGridSettingsMarginType.custom) {
                  handleInputChange({
                    marginWidth: next,
                    marginHeight: customMargin.height,
                    marginType: DatasheetGridSettingsMarginType.custom,
                  });
                }
              }
            }}
          />
          x
          <FormControl
            {...getTestAttributes({ id: 'tx-grid-operation-toolbar-menu-custom-marginy' })}
            disabled={settingsLocal.marginType !== DatasheetGridSettingsMarginType.custom}
            type="number"
            className="DatasheetsEditorHeaderGridSettings__margins-section__input"
            value={customMarginInput.height}
            min={1}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const raw = e.target.value;
              const sanitized = sanitizePositiveIntInput(raw);
              setCustomMarginInput((prev) => ({ ...prev, height: sanitized }));
              const next = Number(sanitized);
              if (Number.isFinite(next) && next > 0) {
                setCustomMargin((prev) => ({ ...prev, height: next }));
                if (settingsLocal.marginType === DatasheetGridSettingsMarginType.custom) {
                  handleInputChange({
                    marginWidth: customMargin.width,
                    marginHeight: next,
                    marginType: DatasheetGridSettingsMarginType.custom,
                  });
                }
              }
            }}
          />
        </Flex>
      </div>

      {gridControls.map((x) => (
        <Flex key={x.label} alignItems="center" justify="space-between" className="DatasheetsEditorHeaderGridSettings__section">
          <span>{x.label}</span>
          <FormControl
            {...getTestAttributes({ id: x.id })}
            type="number"
            className="DatasheetsEditorHeaderGridSettings__grid-input"
            value={x.inputValue ?? x.value}
            onChange={x.onChange}
            min={1}
          />
        </Flex>
      ))}
    </div>
  );
};
